import { ToolMessage, type MessageContent } from "@langchain/core/messages";
import type { AgentThread } from "src/stores/agent";
import { StreamContextType } from "../Stream";
import {
  type ChatMessage,
  type ChatToolMessage,
  getChatMessageType,
} from "../types";

export const getUserQueryPart = (message: string) => {
  // Handle empty or whitespace-only messages
  if (!message.trim()) {
    return "";
  }

  // Find all user-query matches
  const allMatches = [...message.matchAll(/<user-query>(.*?)<\/user-query>/gs)];

  if (allMatches.length === 0) {
    return message;
  }

  // Get the last match
  const lastMatch = allMatches[allMatches.length - 1];
  const lastMatchEnd = lastMatch.index! + lastMatch[0].length;

  // Check if the last match is at the end (allowing for trailing whitespace)
  const remainingText = message.slice(lastMatchEnd).trim();

  if (remainingText === "") {
    return lastMatch[1].trim();
  }

  // Return original message if last user-query is not at the end
  return message;
};

/**
 * Extracts a string summary from a message's content, supporting multimodal (text, image, file, etc.).
 * - If text is present, returns the joined text.
 * - If not, returns a label for the first non-text modality (e.g., 'Image', 'Other').
 * - If unknown, returns 'Multimodal message'.
 */
export function getContentString(content: MessageContent): string {
  if (typeof content === "string") return content;
  const texts = content
    .filter((c): c is { type: "text"; text: string } => c.type === "text")
    .map((c) => c.text);
  return texts.join(" ");
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Remove the data:...;base64, prefix
      resolve(result.split(",")[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function valueToString(condition: boolean, value: unknown): string {
  return condition ? String(value) : JSON.stringify(value, null);
}

export const CHAT_SUPPORTED_FILE_TYPES =
  ".md,.txt,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.png,.jpg,.jpeg";

export function isThreadWithValues(thread: AgentThread): boolean {
  return (
    thread.values !== null &&
    typeof thread.values === "object" &&
    "messages" in thread.values &&
    Array.isArray((thread.values as any).messages)
  );
}

export const getThreadContent = (messages: StreamContextType["messages"]) => {
  if (messages.length > 0) {
    return getUserQueryPart(getContentString(messages[0].content));
  }
  return "Untitled";
};

export const mapToolResultToToolCall = (messages: ChatMessage[]) => {
  const results = new Map<string, Map<string, ChatToolMessage>>();
  let currentAiMessageId: string | undefined;

  for (const msg of messages) {
    if (getChatMessageType(msg) === "ai") {
      currentAiMessageId = msg.id;
      if (currentAiMessageId) {
        results.set(currentAiMessageId, new Map<string, ChatToolMessage>());
      }
      continue;
    }

    const toolMessage = msg as unknown as ChatToolMessage;
    if (
      currentAiMessageId &&
      (ToolMessage.isInstance(msg) || getChatMessageType(msg) === "tool") &&
      toolMessage.tool_call_id
    ) {
      results.get(currentAiMessageId)?.set(toolMessage.tool_call_id, toolMessage);
    }
  }
  return results;
};
